import React, {ReactElement, createRef, RefObject} from 'react';
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import MComponent from '../Toolkit/Components/Component';
import {MFormOutcome} from '../Toolkit/Components/Controls/Form';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import MButton from '../Toolkit/Components/Controls/Button';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import MAlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import AlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import WithRouterProps from '../Toolkit/WithRouterProps';
import {toErrorMessage, MNullable} from '../Toolkit/Utils';
import WithRouter from '../Toolkit/WithRouter';
import {CreateSafeDigReply, CreateSafeDigValidator, ProjectReply, SafeDigReply} from '../Api/SafeDigTypes';
import * as Yup from 'yup';
import {MCloseIconName, MSaveIconName, MNavigateToIconName, MSuccessIcon, MNewIconName} from '../Toolkit/Icons';
import MForm from '../Toolkit/Components/Controls/Form';
import {FieldProps, FormikHelpers} from 'formik';
import MFormSection from '../Toolkit/Components/Controls/FormSection';
import MFormFieldControl from '../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../Toolkit/Components/Controls/TextBox';
import MCalendar from '../Toolkit/Components/Controls/Calendar';
import MNumericBox from './Components/NumericBox';
import {SafeDig} from '../Api/SafeDigTypes';
import {CreateSafeDigRequest, SafeDigClient} from '../Api/SafeDigsClients';
import {Divider} from 'primereact/divider';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';
import MDropDown from '../Toolkit/Components/Controls/DropDown';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../Api/SafeDigAccessTokenProvider';
import MTextArea from './Components/TextArea';
import {JobAreas} from './SafeDigConstants';

/** */
type RouteParams = {
    safedigId: number,
    subCount: number,
};

/** */
type CreateSafeDigData = {
    estimate: string,
    project: number,
    requestor: string,
    address1: string,
    address2: string,
    address3: string,
    county: string,
    postcode: string,
    workDescription: string,
    northing: number,
    easting: number,
    northing2: number,
    easting2: number,
    parent: number,
    requiredDate: MNullable<Date>
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    page: MPageProps;
    form: {
        id: string;
        data: CreateSafeDigData;
        isSubmitting: boolean;
        validationSchema: any;
    }
    projectList: MDropDownItem<number>[];
}

class CreateSafeDigPage extends MComponent<Props, State> {
    private m_newSafeDig: MNullable<SafeDig> | undefined;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_dialogRef: RefObject<MAlertDialog>;
    private readonly m_formRef: RefObject<MForm<CreateSafeDigData>>;
    private readonly m_safedigClient: NSwagAdapter<SafeDigClient>;
    private readonly m_estimateRef: RefObject<MTextBox>;
    private readonly m_areaRef: RefObject<MDropDown<string>>;
    private m_projectValue: number;
    private m_areaValue: string;

    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_pageRef = createRef<MPage>();
        this.m_dialogRef = createRef<MAlertDialog>();
        this.m_formRef = createRef<MForm<CreateSafeDigData>>();
        this.m_safedigClient = new NSwagAdapter<SafeDigClient>(new SafeDigClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_SAFEDIGS_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_estimateRef = createRef<MTextBox>();
        this.m_areaRef = createRef<MDropDown<string>>();
        this.m_projectValue = 1;
        this.m_areaValue = 'Aintree';
        //this.m_newSafeDig = null;

        this.initializeState();
    }

    private bindMethods(): void {
        this.createNoticeButton_onClick = this.createNoticeButton_onClick.bind(this);
        this.showSafeDigCreatedDialog = this.showSafeDigCreatedDialog.bind(this);
        this.isFormDisabled = this.isFormDisabled.bind(this);

        this.close_Click = this.close_Click.bind(this);
        this.navigateToSafeDigButton_onClick = this.navigateToSafeDigButton_onClick.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
    }

    private initializeState(): void {
        Yup.addMethod(Yup.string, 'checkDuplicate', function validateEstimate() {
            return this.test('estimate-duplicate', 'ID must be unique', function(value) {
                return new Promise((resolve, reject) => {
                    if(value === 'Test') {
                        reject('ID must be unique');
                    }
                    resolve(true);
                });
            });
        });

        const page: MPageProps = {locked: false};

        const data: CreateSafeDigData = {
            estimate: '',
            project: 1,
            requestor: '',
            address1: '',
            address2: '',
            address3: '',
            county: '',
            postcode: '',
            workDescription: '',
            northing: 0,
            easting: 0,
            northing2: 0,
            easting2: 0,
            parent: -1,
            requiredDate: null
        };

        const validationSchema: any = Yup.object().shape({
            estimate: CreateSafeDigValidator.estimateFunc(this.m_safedigClient),
            requestor: CreateSafeDigValidator.requestor,
            address1: CreateSafeDigValidator.address1,
            address2: CreateSafeDigValidator.address2,
            address3: CreateSafeDigValidator.address3,
            postcode: CreateSafeDigValidator.postcode,
            workDescription: CreateSafeDigValidator.workDescription,
            northing: CreateSafeDigValidator.northing,
            easting: CreateSafeDigValidator.easting,
            northing2: CreateSafeDigValidator.northing2,
            easting2: CreateSafeDigValidator.easting2,
            requiredDate: CreateSafeDigValidator.requiredDate
        });

        this.state = {
            page: page,
            form: {
                id: 'createSafeDigForm',
                data: data,
                isSubmitting: false,
                validationSchema: validationSchema
            },
            projectList: []
        };
    }

    public componentDidMount(): void {
        //Use the currently logged in user as the default requestor.
        const userName = this.props.msalContext.accounts[0].name;
        this.safeSetState((draft) => {
            draft.form.data.requestor = userName!;
        });
    }

    private showSafeDigCreatedDialog(): void {

        this.m_dialogRef.current!.show(
            'Create Safe Dig',
            `A new safe dig has been created for ${this.m_newSafeDig!.estimate}. To create a Notice click below or open the new safe dig to add files.`,
            MSuccessIcon,
            <div>
                <MButton label="Create Notice"
                         color="success"
                         icon={MNewIconName}
                         disabled={false}
                         onClick={this.createNoticeButton_onClick}/>
                <MButton label="Open new safe dig"
                         color="success"
                         icon={MNavigateToIconName}
                         disabled={false}
                         onClick={this.navigateToSafeDigButton_onClick}/>
            </div>);

    }

    private createNoticeButton_onClick(): void {
        this.props.history.replace(`/notices/create/0/${this.m_newSafeDig?.safeDigsId}/0`);
    }

    private navigateToSafeDigButton_onClick(): void {
        this.props.history.replace(`/safedigs/${this.m_newSafeDig?.safeDigsId}`);
    }

    private isFormDisabled(): boolean {
        return this.state.form.isSubmitting;
    }

    /** */
    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    private form_onBeforeSubmit(fieldValues: CreateSafeDigData, formikHelpers: FormikHelpers<CreateSafeDigData>): boolean {
        const cancel = false;
        this.m_pageRef.current!.messageBar.close();
        this.safeSetState((draft: State) => draft.form.isSubmitting = true);
        return cancel;

    }

    private async form_onSubmit(fieldValues: CreateSafeDigData, formikHelpers: FormikHelpers<CreateSafeDigData>): Promise<void> {
        const userName = this.props.msalContext.accounts[0].name;

        const createRequest = new CreateSafeDigRequest({
            estimate: fieldValues.estimate.replace(/"/g, ''),
            project: this.m_projectValue,
            requestor: fieldValues.requestor,
            address1: fieldValues.address1.replace(/"/g, ''),
            address2: fieldValues.address2.replace(/"/g, ''),
            address3: fieldValues.address3.replace(/"/g, ''),
            county: this.m_areaValue,
            postcode: fieldValues.postcode.replace(/"/g, ''),
            workDescription: fieldValues.workDescription.replace(/"/g, ''),
            northing: fieldValues.northing,
            easting: fieldValues.easting,
            northing2: fieldValues.northing2,
            easting2: fieldValues.easting2,
            requiredDate: fieldValues.requiredDate!,
            user: userName,
            parent: fieldValues.parent
        });

        const result = await this.m_safedigClient.callWithReply(CreateSafeDigReply, (client) => client.post(createRequest));

        result.throwIfFailed();

        this.m_newSafeDig = result.reply!.data;
    }

    /** */
    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: CreateSafeDigData, formikHelpers: FormikHelpers<CreateSafeDigData>, exception: any | undefined): void {

        if(outcome === 'success') {

            this.showSafeDigCreatedDialog();
            return;

        }

        this.safeSetState(
            (draft: State) => draft.form.isSubmitting = false);

    }

    /** */
    private form_onError(exception: any): boolean {

        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;

    }

    private async page_onLoadData(): Promise<void> {
        const safeDigId = this.props.match.params.safedigId;
        const subCount = this.props.match.params.subCount;
        const subCountTag = subCount !== undefined ? (Number(subCount) + 1).toString().padStart(3, '0') : '000';
        if(safeDigId !== undefined) {
            //Create sub job requested so get data from existing job.
            const digReply = await this.m_safedigClient.callWithReply(
                SafeDigReply, (client) => client.get(safeDigId));
            digReply.throwIfFailed();
            this.safeSetState((draft) => {
                draft.form.data.estimate = `${digReply.reply!.data!.estimate}-S${subCountTag}`;
                draft.form.data.project = digReply.reply!.data!.project;
                draft.form.data.parent = digReply.reply!.data!.safeDigsId;
                draft.form.data.easting = digReply.reply!.data!.easting;
                draft.form.data.northing = digReply.reply!.data!.northing;
                draft.form.data.easting2 = digReply.reply!.data!.easting2;
                draft.form.data.northing2 = digReply.reply!.data!.northing2;
                draft.form.data.address1 = digReply.reply!.data!.address1;
                draft.form.data.address2 = digReply.reply!.data!.address2;
                draft.form.data.address3 = digReply.reply!.data!.address3;
                draft.form.data.county = digReply.reply!.data!.county;
                draft.form.data.postcode = digReply.reply!.data!.postcode;
            });
            this.m_areaRef.current!.setValue(JobAreas.includes(digReply.reply!.data!.county) ? digReply.reply!.data!.county : 'Other');
            this.m_areaValue = digReply.reply!.data!.county;
        }

        //Get current project list
        const projectResult = await this.m_safedigClient.callWithReply(
            ProjectReply, (client) => client.getProjects());

        projectResult.throwIfFailed();

        const listRows = projectResult.reply!.data.map(project => ({
            value: project.id,
            label: project.name
        }));

        this.safeSetState((draft) => {
            draft.projectList = listRows;
            draft.form.data.project = 1;
        });
    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton label="Save"
                         formId={this.state.form.id}
                         type="submit"
                         color="success"
                         icon={MSaveIconName}
                         iconOnly={false}
                         isBusy={this.isFormDisabled}
                         disabled={this.isFormDisabled}/>
                <MSpacer/>
                <MButton label="Close"
                         icon={MCloseIconName}
                         disabled={this.isFormDisabled}
                         onClick={this.close_Click}/>
            </MToolbar>
        );

    }

    public render(): ReactElement {
        return (
            <MPage title="Create Safe Dig"
                   subtitle="Create a new Safe Dig request"
                   locked={this.state.page.locked}
                   width={8}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>
                <AlertDialog ref={this.m_dialogRef}/>

                <MForm formId={this.state.form.id}
                       initialValues={this.state.form.data}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       onSubmit={this.form_onSubmit}
                       onError={this.form_onError}
                       onAfterSubmit={this.form_onAfterSubmit}
                       ref={this.m_formRef}>

                    <div className="formgrid grid">
                        <div className="col-12">
                            <MFormSection title="General">

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="ID"
                                                       fieldName="estimate">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <React.Fragment>
                                                    <MTextBox id={fieldProps.field.name}
                                                              maxLength={30}
                                                              fieldProps={fieldProps}
                                                              disabled={this.isFormDisabled}
                                                              className={fieldClasses}
                                                              ref={this.m_estimateRef}/>
                                                </React.Fragment>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label={'Project'}
                                                       fieldName={'project'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<number> id="ProjectSelection"
                                                                   disabled={false}
                                                                   items={this.state.projectList}
                                                                   initialValue={this.state.form.data.project}
                                                                   onChange={(e) => this.m_projectValue = e! }
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Requestor"
                                                       fieldName="requestor">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={100}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}
                                                          initialValue={this.state.form.data.requestor}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Required Date"
                                                       fieldName="requiredDate">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MCalendar id={fieldProps.field.name}
                                                           fieldProps={fieldProps}
                                                           disabled={this.isFormDisabled}
                                                           className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-12">
                                    <MFormFieldControl label="Work Description"
                                                       fieldName="workDescription">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MTextArea id={fieldProps.field.name}
                                                           maxLength={500}
                                                           fieldProps={fieldProps}
                                                           disabled={this.isFormDisabled}
                                                           className={fieldClasses}
                                                           rows={5}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>
                            </MFormSection>
                        </div>

                        <div className="col-12">
                            <Divider/>
                            <MFormSection title="Location">

                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="Start location - Eastings"
                                                       fieldName="easting">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="Start location - Northings"
                                                       fieldName="northing">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="End location - Eastings"
                                                       fieldName="easting2">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-6">
                                    <MFormFieldControl label="End location - Northings"
                                                       fieldName="northing2">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MNumericBox id={fieldProps.field.name}
                                                             maxLength={8}
                                                             fieldProps={fieldProps}
                                                             disabled={this.isFormDisabled}
                                                             className={fieldClasses}
                                                             decimalPlaces={0}
                                                             useGrouping={false}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>
                            </MFormSection>
                        </div>

                        <div className="col-12">
                            <Divider/>
                            <MFormSection title="Address">

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Address1"
                                                       fieldName="address1">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={50}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Address2"
                                                       fieldName="address2">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={50}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Address3"
                                                       fieldName="address3">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={50}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Build Area"
                                                       fieldName="county">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<string> id="AreaSelection"
                                                                   ref={this.m_areaRef}
                                                                   disabled={false}
                                                                   items={JobAreas.map(area => ({
                                                                       value: area,
                                                                       label: area
                                                                   }))}
                                                                   initialValue={'Aintree'}
                                                                   onChange={(e) => this.m_areaValue = e! }
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Postcode"
                                                       fieldName="postcode">
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={50}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>
                            </MFormSection>
                        </div>
                    </div>
                </MForm>
            </MPage>
        );
    }
}

export default withMsal(WithRouter(CreateSafeDigPage));